import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ArrowRightLeft, Plus, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import { formatDate, formatCurrency } from '../lib/constants';

const TransferPage = () => {
  const [transfers, setTransfers] = useState([]);
  const [items, setItems] = useState([]);
  const [locations, setLocations] = useState([]);
  const [stocks, setStocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [form, setForm] = useState({
    item_id: '',
    from_location: 'PUSAT',
    to_location: '',
    quantity: 1,
    notes: '',
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [transfersRes, itemsRes, locationsRes, stocksRes] = await Promise.all([
        api.get('/warehouse/transfers'),
        api.get('/items', { params: { include_all: true } }),
        api.get('/locations'),
        api.get('/warehouse/stock'),
      ]);
      setTransfers(transfersRes.data);
      setItems(itemsRes.data);
      setLocations(locationsRes.data);
      setStocks(stocksRes.data);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleTransfer = async () => {
    try {
      await api.post('/warehouse/transfer', {
        ...form,
        quantity: parseInt(form.quantity),
      });
      toast.success('Transfer stok berhasil');
      setShowDialog(false);
      setForm({ item_id: '', from_location: 'PUSAT', to_location: '', quantity: 1, notes: '' });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal transfer stok');
    }
  };

  const getStockAtLocation = (itemId, locationId) => {
    const stock = stocks.find((s) => s.item_id === itemId && s.location_id === locationId);
    return stock?.quantity || 0;
  };

  const availableStock = form.item_id && form.from_location
    ? getStockAtLocation(form.item_id, form.from_location)
    : 0;

  return (
    <Layout>
      <div className="space-y-6" data-testid="transfer-page">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground mb-2">Gudang</p>
            <h1 className="text-4xl font-medium tracking-tight">Transfer Stok</h1>
            <p className="text-muted-foreground mt-1">Transfer barang dari Gudang Pusat ke lokasi cabang</p>
          </div>
          <Button
            onClick={() => setShowDialog(true)}
            className="bg-primary hover:bg-primary/90"
            data-testid="new-transfer-button"
          >
            <Plus className="w-4 h-4 mr-2" />
            Transfer Baru
          </Button>
        </div>

        <Card className="border-border">
          <CardHeader className="border-b border-border">
            <CardTitle className="text-xl tracking-tight font-medium">Riwayat Transfer</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-12 text-center">
                <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent"></div>
              </div>
            ) : transfers.length === 0 ? (
              <div className="p-12 text-center text-muted-foreground">
                <ArrowRightLeft className="w-16 h-16 mx-auto mb-4 opacity-30" />
                <p>Belum ada transfer stok</p>
              </div>
            ) : (
              <div className="divide-y divide-border">
                {transfers.map((t, idx) => (
                  <div key={idx} className="p-5" data-testid={`transfer-row-${idx}`}>
                    <div className="flex items-start gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 mb-2 flex-wrap">
                          <span className="font-mono text-xs bg-secondary px-2 py-1 rounded">
                            {t.transfer_id || 'TRF-XXX'}
                          </span>
                          {t.invoice_number && (
                            <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-300">
                              Invoice: {t.invoice_number}
                            </Badge>
                          )}
                        </div>
                        <p className="font-medium mb-1">{t.item_name}</p>
                        <div className="flex items-center gap-3 text-sm text-muted-foreground flex-wrap">
                          <Badge variant="outline" className="font-mono">{t.from_location}</Badge>
                          <ArrowRight className="w-4 h-4 text-primary" />
                          <Badge variant="outline" className="font-mono">{t.to_location}</Badge>
                          <span className="font-medium text-foreground">
                            {t.quantity} {t.unit}
                          </span>
                        </div>
                        {t.notes && <p className="text-xs text-muted-foreground mt-2">Catatan: {t.notes}</p>}
                        <p className="text-xs text-muted-foreground mt-1">{formatDate(t.created_at)}</p>
                      </div>
                      {t.total_cost > 0 && (
                        <div className="text-right">
                          <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Total Biaya</p>
                          <p className="text-lg font-medium text-primary">{formatCurrency(t.total_cost)}</p>
                          <p className="text-xs text-muted-foreground">
                            @{formatCurrency(t.unit_price || 0)}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Transfer Stok Antar Lokasi</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Barang *</Label>
                <Select value={form.item_id} onValueChange={(v) => setForm({ ...form, item_id: v })}>
                  <SelectTrigger data-testid="transfer-item-select">
                    <SelectValue placeholder="Pilih barang" />
                  </SelectTrigger>
                  <SelectContent>
                    {items.map((item) => (
                      <SelectItem key={item.id} value={item.id}>
                        {item.name} ({item.unit})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Dari Lokasi *</Label>
                  <Select value={form.from_location} onValueChange={(v) => setForm({ ...form, from_location: v })}>
                    <SelectTrigger data-testid="transfer-from-select">
                      <SelectValue placeholder="Pilih asal" />
                    </SelectTrigger>
                    <SelectContent>
                      {locations.map((loc) => (
                        <SelectItem key={loc.id} value={loc.id}>
                          {loc.name} {loc.is_warehouse ? '(Gudang)' : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Ke Lokasi *</Label>
                  <Select value={form.to_location} onValueChange={(v) => setForm({ ...form, to_location: v })}>
                    <SelectTrigger data-testid="transfer-to-select">
                      <SelectValue placeholder="Pilih tujuan" />
                    </SelectTrigger>
                    <SelectContent>
                      {locations
                        .filter((l) => l.id !== form.from_location)
                        .map((loc) => (
                          <SelectItem key={loc.id} value={loc.id}>
                            {loc.name} {loc.is_warehouse ? '(Gudang)' : ''}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Jumlah *</Label>
                <Input
                  type="number"
                  min="1"
                  max={availableStock}
                  value={form.quantity}
                  onChange={(e) => setForm({ ...form, quantity: e.target.value })}
                  data-testid="transfer-quantity-input"
                />
                {form.item_id && form.from_location && (
                  <p className="text-xs text-muted-foreground">
                    Stok tersedia di {form.from_location}: <span className="font-medium text-foreground">{availableStock}</span>
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label>Catatan</Label>
                <Textarea
                  placeholder="Catatan transfer (opsional)..."
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  rows={2}
                  data-testid="transfer-notes-input"
                />
              </div>

              {form.item_id && form.quantity > 0 && (() => {
                const selectedItem = items.find((i) => i.id === form.item_id);
                if (!selectedItem) return null;
                const totalCost = form.quantity * selectedItem.price;
                return (
                  <div className="p-4 rounded-lg bg-primary/5 border border-primary/20">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground">Rincian Invoice</p>
                      <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-300 text-xs">
                        Invoice Otomatis
                      </Badge>
                    </div>
                    <div className="flex items-center justify-between text-sm mb-1">
                      <span className="text-muted-foreground">
                        {form.quantity} × {formatCurrency(selectedItem.price)}
                      </span>
                      <span className="font-medium">{formatCurrency(totalCost)}</span>
                    </div>
                    <div className="flex items-center justify-between pt-2 mt-2 border-t border-primary/20">
                      <span className="font-medium">Total dibebankan ke {form.to_location || '...'}</span>
                      <span className="text-lg font-medium text-primary">{formatCurrency(totalCost)}</span>
                    </div>
                  </div>
                );
              })()}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDialog(false)}>Batal</Button>
              <Button
                onClick={handleTransfer}
                disabled={!form.item_id || !form.to_location || form.quantity < 1 || form.quantity > availableStock}
                className="bg-primary hover:bg-primary/90"
                data-testid="save-transfer-button"
              >
                Transfer
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default TransferPage;
